export const LIBRARY_DISPLAY_NAME = "Guidance Library";

export const LIBRARY_SUBTITLE =
  "Practices, pitfalls, and product guidance for sponsors, account teams, and program leaders";

export type AudienceTab = "sponsors" | "account_teams" | "program_leaders";

export type SponsorSubTab =
  | "recommended"
  | "saved"
  | "by_category"
  | "what_to_avoid"
  | "playbooks"
  | "by_level"
  | "by_seniority"
  | "all";

export type AccountTeamSubTab =
  | "by_category"
  | "saved"
  | "what_to_avoid"
  | "by_role"
  | "all";

export type ProgramLeaderSubTab =
  | "by_category"
  | "saved"
  | "what_to_avoid"
  | "all";

export type LibrarySubTab =
  | SponsorSubTab
  | AccountTeamSubTab
  | ProgramLeaderSubTab;

export const AUDIENCE_API_MAP: Record<AudienceTab, string> = {
  sponsors: "sponsor",
  account_teams: "account_team",
  program_leaders: "program_leader",
};

export const AUDIENCE_TABS: { id: AudienceTab; label: string }[] = [
  { id: "sponsors", label: "Sponsors" },
  { id: "account_teams", label: "Account Teams" },
  { id: "program_leaders", label: "Program Leaders" },
];

export const SPONSOR_SUB_TABS: { id: SponsorSubTab; label: string }[] = [
  { id: "recommended", label: "Recommended for you" },
  { id: "saved", label: "Saved" },
  { id: "by_category", label: "By category" },
  { id: "what_to_avoid", label: "What to avoid" },
  { id: "playbooks", label: "Playbooks" },
  { id: "by_level", label: "By XFACTOR Account Level" },
  { id: "by_seniority", label: "By seniority" },
  { id: "all", label: "All guidance" },
];

export const ACCOUNT_TEAM_SUB_TABS: { id: AccountTeamSubTab; label: string }[] =
  [
    { id: "by_category", label: "By category" },
    { id: "saved", label: "Saved" },
    { id: "what_to_avoid", label: "What to avoid" },
    { id: "by_role", label: "By role" },
    { id: "all", label: "All guidance" },
  ];

export const PROGRAM_LEADER_SUB_TABS: {
  id: ProgramLeaderSubTab;
  label: string;
}[] = [
  { id: "by_category", label: "By category" },
  { id: "saved", label: "Saved" },
  { id: "what_to_avoid", label: "What to avoid" },
  { id: "all", label: "All guidance" },
];

export const INITIAL_RECOMMENDED = 8;

export const CATEGORY_LABELS: Record<string, string> = {
  relationship_fundamentals: "Relationship fundamentals",
  meeting_cadence: "Meeting cadence",
  preparation_presence: "Preparation & presence",
  escalation_advocacy: "Escalation & advocacy",
  seniority_vp_svp: "VP / SVP",
  seniority_estaff: "E-Staff",
  seniority_cstaff: "C-Staff",
  level_progression: "Account Level progression",
  soft_skills: "Soft skills",
  meeting_formats: "Meeting formats",
  failure_modes: "Failure modes",
  eb_feature: "ExecBridge features",
  sponsor_journey_orientation: "Customer journey — Orientation",
  sponsor_journey_pre_sales: "Customer journey — Pre-sales",
  sponsor_journey_onboarding: "Customer journey — Onboarding",
  sponsor_journey_steady_state: "Customer journey — Steady state",
  sponsor_journey_expansion: "Customer journey — Expansion",
  sponsor_journey_renewal: "Customer journey — Renewal",
  sponsor_account_team_collaboration: "Account team collaboration",
  at_team_orientation: "Team orientation",
  at_sponsor_collaboration: "Sponsor collaboration",
  at_customer_exec_support: "Customer exec support",
  at_counterpart_engagement: "Counterpart engagement",
  at_escalation_hygiene: "Escalation hygiene",
  at_eb_features: "ExecBridge features",
  pl_program_foundation: "Program foundation",
  pl_sponsor_matching: "Sponsor matching",
  pl_sponsor_reassignment: "Sponsor reassignment",
  pl_sponsor_enablement: "Sponsor enablement",
  pl_governance: "Governance",
  pl_measurement: "Measurement",
  pl_cross_function: "Cross-function",
  pl_eb_operations: "ExecBridge operations",
};

export const JOURNEY_CATEGORIES = new Set([
  "sponsor_journey_orientation",
  "sponsor_journey_pre_sales",
  "sponsor_journey_onboarding",
  "sponsor_journey_steady_state",
  "sponsor_journey_expansion",
  "sponsor_journey_renewal",
]);

export const SPONSOR_JOURNEY_CATEGORIES = [
  "sponsor_journey_orientation",
  "sponsor_journey_pre_sales",
  "sponsor_journey_onboarding",
  "sponsor_journey_steady_state",
  "sponsor_journey_expansion",
  "sponsor_journey_renewal",
] as const;

export const CUSTOMER_JOURNEY_GROUP = "Customer journey";
export const SPONSOR_JOURNEY_BROWSE_KEY = "sponsor_journey";

const LIBRARY_LAST_SUBTAB_KEY = "eb-guidance-last-subtab";
const LIBRARY_LAST_VIEW_KEY = "eb-guidance-last-view";

/** data attribute on the library browse scroll container */
export const LIBRARY_BROWSE_SCROLL_ATTR = "data-library-browse-scroll";

export type LibraryReturnState = {
  tab?: AudienceTab;
  subtab?: string;
  /** Hash anchor without leading #, e.g. category-pl_eb_operations */
  hash?: string;
  /** Scroll offset within the library browse container */
  scrollTop?: number;
};

export const readLibraryBrowseScrollTop = (): number | undefined => {
  const root = document.querySelector(`[${LIBRARY_BROWSE_SCROLL_ATTR}]`);
  if (!(root instanceof HTMLElement)) return undefined;
  return root.scrollTop > 0 ? root.scrollTop : undefined;
};

export const saveLibrarySubtab = (subtab: string): void => {
  try {
    sessionStorage.setItem(LIBRARY_LAST_SUBTAB_KEY, subtab);
  } catch {
    /* ignore */
  }
};

export const getLibrarySubtab = (): string => {
  try {
    return sessionStorage.getItem(LIBRARY_LAST_SUBTAB_KEY) || "recommended";
  } catch {
    return "recommended";
  }
};

export const saveLibraryViewState = (state: LibraryReturnState): void => {
  try {
    sessionStorage.setItem(LIBRARY_LAST_VIEW_KEY, JSON.stringify(state));
    if (state.subtab) {
      sessionStorage.setItem(LIBRARY_LAST_SUBTAB_KEY, state.subtab);
    }
  } catch {
    /* ignore */
  }
};

export const getLibraryViewState = (): LibraryReturnState => {
  try {
    const raw = sessionStorage.getItem(LIBRARY_LAST_VIEW_KEY);
    if (raw) return JSON.parse(raw) as LibraryReturnState;
  } catch {
    /* ignore */
  }
  return { subtab: getLibrarySubtab() };
};

/** Default library browse context for a tip (e.g. email deep links with no fromLibrary state). */
export const inferLibraryReturnStateFromTip = (
  tip: Pick<SponsorTip, "audience" | "category">
): LibraryReturnState => {
  const tab: AudienceTab =
    tip.audience === "program_leader"
      ? "program_leaders"
      : tip.audience === "account_team"
        ? "account_teams"
        : "sponsors";

  return {
    tab,
    subtab: "by_category",
    hash: tip.category ? `category-${tip.category}` : undefined,
  };
};

export const buildLibraryReturnHref = (
  basePath: string,
  state: LibraryReturnState
): string => {
  const params = new URLSearchParams();
  const tab = state.tab || "sponsors";

  if (tab !== "sponsors") {
    params.set("tab", tab);
  }
  if (state.subtab) {
    params.set("subtab", state.subtab);
  }

  const query = params.toString();
  const hash = state.hash ? `#${state.hash}` : "";
  return `${basePath}${query ? `?${query}` : ""}${hash}`;
};

export const readLibraryReturnStateFromLocation = (
  searchParams: URLSearchParams,
  hash = window.location.hash
): LibraryReturnState => {
  const tabParam = searchParams.get("tab");
  const tab: AudienceTab =
    tabParam === "account_teams" || tabParam === "program_leaders"
      ? tabParam
      : "sponsors";
  const subtab = searchParams.get("subtab") || getLibrarySubtab();
  const hashKey = hash.replace(/^#/, "");
  const scrollTop = readLibraryBrowseScrollTop();

  return {
    tab,
    subtab,
    hash: hashKey || undefined,
    scrollTop,
  };
};

export const LIBRARY_TYPE = {
  viewTitle: "text-lg font-semibold text-gray-900",
  viewHelp: "text-sm text-gray-600 leading-relaxed",
  sectionTitle: "text-base font-semibold text-gray-900",
  sectionDesc: "text-xs text-gray-600 mt-1.5 leading-relaxed",
  cardTitle: "text-sm font-semibold text-gray-900",
  cardBody: "text-xs text-gray-600 leading-relaxed",
};

export const LIBRARY_ARTICLE_TYPE = {
  articleBody: "text-base text-gray-700 leading-relaxed",
  pairedLabel: "text-xs font-semibold uppercase tracking-wide text-gray-500",
  pairedBody: "text-sm text-gray-600 leading-relaxed",
};

export const CATEGORY_ORDER = [
  SPONSOR_JOURNEY_BROWSE_KEY,
  "sponsor_account_team_collaboration",
  "relationship_fundamentals",
  "meeting_cadence",
  "preparation_presence",
  "escalation_advocacy",
  "soft_skills",
  "meeting_formats",
  "level_progression",
  "seniority_vp_svp",
  "seniority_estaff",
  "seniority_cstaff",
  "eb_feature",
  "failure_modes",
];

export const ACCOUNT_TEAM_CATEGORY_ORDER = [
  "at_team_orientation",
  "at_sponsor_collaboration",
  "at_customer_exec_support",
  "at_counterpart_engagement",
  "at_escalation_hygiene",
  "at_eb_features",
];

export const PROGRAM_LEADER_CATEGORY_ORDER = [
  "pl_program_foundation",
  "pl_sponsor_matching",
  "pl_sponsor_reassignment",
  "pl_sponsor_enablement",
  "pl_governance",
  "pl_measurement",
  "pl_cross_function",
  "pl_eb_operations",
];

export const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  [SPONSOR_JOURNEY_BROWSE_KEY]:
    "Map, orientation, and phase-by-phase sponsor responsibilities across the customer lifecycle",
  sponsor_journey_orientation:
    "Lifecycle map and sponsor POV across the customer journey",
  sponsor_journey_pre_sales: "When to join deal motions — and when to stay out",
  sponsor_journey_onboarding:
    "Executive relationship formation in the first 90 days",
  sponsor_journey_steady_state:
    "Cadence and maintenance between renewals and crises",
  sponsor_journey_expansion: "Peer altitude on strategic expansion moments",
  sponsor_journey_renewal:
    "Relationship depth before the commercial renewal process",
  sponsor_account_team_collaboration:
    "Working with AE, CSM, and the broader vendor team",
  relationship_fundamentals: "Core relationship building principles",
  meeting_cadence: "Frequency, consistency, and touchpoint management",
  preparation_presence: "How to prepare and show up",
  escalation_advocacy: "Using internal influence effectively",
  seniority_vp_svp: "VP/SVP-specific guidance",
  seniority_estaff: "E-staff and EVP-specific guidance",
  seniority_cstaff: "C-suite-specific guidance",
  level_progression: "XFACTOR Account Level (AL) advancement playbooks",
  soft_skills: "Executive presence, listening, and communication",
  meeting_formats: "EBR, async, on-site, and other formats",
  failure_modes: "Common pitfalls and what to avoid",
  eb_feature: "Getting more from ExecBridge capabilities",
  at_team_orientation: "AE, CSM, and sponsor roles across the customer journey",
  at_sponsor_collaboration:
    "Briefing, sync rhythm, and internal alignment with your sponsor",
  at_customer_exec_support:
    "One external voice and making the sponsor effective in the room",
  at_counterpart_engagement:
    "Mapping counterparts and routing signal to the right altitude",
  at_escalation_hygiene:
    "Escalate with judgment — recommendations, not theater",
  at_eb_features: "Logging commitments and shared account memory in ExecBridge",
  pl_program_foundation:
    "Charter, scope, and capacity rules before you assign anyone",
  pl_sponsor_matching:
    "Who should sponsor, multi-factor matching, and calibration",
  pl_sponsor_reassignment:
    "Departures, customer exec changes, and portfolio moves",
  pl_sponsor_enablement:
    "How sponsors learn the role and ramp on inherited portfolios",
  pl_governance: "Operational governance, quality bar, and executive air cover",
  pl_measurement:
    "Coverage, utilization, cadence, and sampled account outcomes",
  pl_cross_function:
    "Training account teams and auditing the sponsor–AE–CSM triad",
  pl_eb_operations:
    "ExecBridge as system of record for assignments, capacity, and handoffs",
};

export const LEVEL_ORDER = [
  "ALL",
  "L0",
  "L1",
  "L2",
  "L3",
  "L4",
  "L5",
  "Unspecified",
];

export const SENIORITY_ORDER = [
  "ALL",
  "VP_SVP",
  "ESTAFF",
  "CSTAFF",
  "Unspecified",
];

export const ROLE_LENS_ORDER = ["Both", "AE", "CSM"];

export const LEVEL_PLAYBOOK_DESCRIPTIONS: Record<string, string> = {
  ALL: "Guidance applicable at every XFACTOR Account Level",
  L0: "Foundation and first mapping (AL 0 — New)",
  L1: "Early relationship establishment (AL 1 — Acquainted)",
  L2: "Developing trust and cadence (AL 2 — Reactive)",
  L3: "Strategic alignment depth (AL 3 — Proactive)",
  L4: "Partnership and advocacy (AL 4 — Referenceable)",
  L5: "Maximum strategic value (AL 5 — Strategic)",
};

export const SPONSOR_SUBTAB_IDS = new Set<string>(
  SPONSOR_SUB_TABS.map(t => t.id)
);
export const ACCOUNT_TEAM_SUBTAB_IDS = new Set<string>(
  ACCOUNT_TEAM_SUB_TABS.map(t => t.id)
);
export const PROGRAM_LEADER_SUBTAB_IDS = new Set<string>(
  PROGRAM_LEADER_SUB_TABS.map(t => t.id)
);
export const AUDIENCE_BROWSE_SUBTAB_IDS = new Set<string>([
  ...ACCOUNT_TEAM_SUB_TABS.map(t => t.id),
  ...PROGRAM_LEADER_SUB_TABS.map(t => t.id),
]);

export const isSponsorJourneyCategory = (category: string): boolean =>
  category === SPONSOR_JOURNEY_BROWSE_KEY || JOURNEY_CATEGORIES.has(category);

export const isSponsorJourneyPhaseCategory = (category: string): boolean =>
  (SPONSOR_JOURNEY_CATEGORIES as readonly string[]).includes(category);

export const CATEGORY_COLORS: Record<string, string> = {
  [SPONSOR_JOURNEY_BROWSE_KEY]:
    "bg-teal-50 text-teal-800 border border-teal-200",
  sponsor_account_team_collaboration:
    "bg-sky-50 text-sky-800 border border-sky-200",
  relationship_fundamentals: "bg-blue-50 text-blue-800 border border-blue-200",
  meeting_cadence: "bg-indigo-50 text-indigo-800 border border-indigo-200",
  preparation_presence: "bg-violet-50 text-violet-800 border border-violet-200",
  escalation_advocacy: "bg-purple-50 text-purple-800 border border-purple-200",
  soft_skills: "bg-fuchsia-50 text-fuchsia-800 border border-fuchsia-200",
  meeting_formats: "bg-pink-50 text-pink-800 border border-pink-200",
  level_progression: "bg-amber-50 text-amber-900 border border-amber-200",
  eb_feature: "bg-purple-50 text-purple-900 border border-purple-200",
  failure_modes: "bg-amber-50 text-amber-900 border border-amber-200",
};

export const categoryBadgeClass = (
  category: string,
  isFailureMode: boolean
): string => {
  if (isFailureMode) {
    return "bg-gray-100 text-gray-600";
  }
  if (isSponsorJourneyCategory(category)) {
    return CATEGORY_COLORS[SPONSOR_JOURNEY_BROWSE_KEY];
  }
  return (
    CATEGORY_COLORS[category] ||
    "bg-gray-100 text-gray-700 border border-gray-200"
  );
};

export const journeyPhaseBadgeClass = (): string =>
  "bg-teal-50/90 text-teal-800 border border-teal-200";

/** Light metadata chips for level, seniority, and similar card footers */
export const META_CHIP_CLASS =
  "inline-flex text-xs font-medium px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-600";

export const ROLE_LENS_CHIP_CLASS =
  "inline-flex text-xs font-medium px-1.5 py-0.5 rounded border border-sky-200 bg-sky-50 text-sky-800";

export const categoryLabel = (category: string): string => {
  if (isSponsorJourneyCategory(category)) return "Customer journey";
  return CATEGORY_LABELS[category] || category || "Tip";
};

export const categoryDescription = (category: string): string => {
  if (category === SPONSOR_JOURNEY_BROWSE_KEY) {
    return CATEGORY_DESCRIPTIONS[SPONSOR_JOURNEY_BROWSE_KEY] || "";
  }
  return CATEGORY_DESCRIPTIONS[category] || "";
};

export const journeyPhaseLabel = (category: string): string => {
  const short: Record<string, string> = {
    sponsor_journey_orientation: "Orientation",
    sponsor_journey_pre_sales: "Pre-sales",
    sponsor_journey_onboarding: "Onboarding",
    sponsor_journey_steady_state: "Steady state",
    sponsor_journey_expansion: "Expansion",
    sponsor_journey_renewal: "Renewal",
  };
  return short[category] || CATEGORY_LABELS[category] || category;
};

/** Display label for a stored tip level key ("L3" → "AL 3"). */
export const levelLabel = (levelKey: string): string => {
  if (levelKey === "ALL") return "All levels";
  const match = /^L([0-9]+)$/i.exec(levelKey);
  return match ? `AL ${match[1]}` : levelKey;
};

export const formatLevelTags = (tip: SponsorTip): string[] => {
  const levels = tip.xfactor_levels || [];
  if (levels.includes("ALL")) return ["All levels"];
  return levels.filter(l => l !== "ALL").map(levelLabel);
};

/** Display-time rewrite of stored "L0"–"L5" mentions in tip prose to "AL 0"–"AL 5". */
export const formatAccountLevelMentionsInText = (
  text?: string | null
): string => (text || "").replace(/\bL([0-5])\b/g, "AL $1");

export const formatSeniorityTags = (tip: SponsorTip): string[] => {
  const sen = tip.seniority || [];
  if (sen.includes("ALL")) return ["All seniorities"];
  return sen.map(s => SENIORITY_LABELS[s] || s);
};

export const formatRoleLensTags = (tip: SponsorTip): string[] => {
  if (!tip.role_lens) return [];
  return [ROLE_LENS_LABELS[tip.role_lens] || tip.role_lens];
};

export const formatEbFeaturePreview = (key?: string | null): string => {
  if (!key) return "";
  if (EB_FEATURE_LABELS[key]) return EB_FEATURE_LABELS[key];
  return key
    .split("_")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

export const getPairedGuidanceCta = (viewingFailureMode: boolean): string =>
  viewingFailureMode ? "Read the practice →" : "See what to avoid →";

export const formatPublished = (publishedAt?: string | null): string | null => {
  if (!publishedAt) return null;
  try {
    return new Date(publishedAt).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
};

export const trackSponsorTipsEvent = (
  event: string,
  properties: Record<string, unknown> = {}
): void => {
  if (process.env.NODE_ENV === "development") {
    console.info("[sponsor-tips telemetry]", event, properties);
  }
};

export const SENIORITY_LABELS: Record<string, string> = {
  VP_SVP: "VP / SVP",
  ESTAFF: "E-Staff",
  CSTAFF: "C-Staff",
  ALL: "All seniority",
};

export const ROLE_LENS_LABELS: Record<string, string> = {
  AE: "AE",
  CSM: "CSM",
  Both: "AE & CSM",
};

export const EB_FEATURE_LABELS: Record<string, string> = {
  relationship_drift_detection: "Relationship drift detection",
  goal_setting_cadence_targets: "Goal setting & cadence targets",
  next_best_action_engine: "Next best action engine",
  ai_premeeting_brief: "AI pre-meeting brief",
  relationship_notes: "Relationship notes",
  ai_followup_draft: "AI follow-up draft",
  sponsor_effectiveness_dashboard: "Sponsor effectiveness dashboard",
  one_insight_generator: "One insight generator",
  commitment_tracker: "Commitment tracker",
  reference_readiness_score: "Reference readiness score",
  relationships_coverage_map: "Relationships coverage map",
  joint_success_plan: "Joint success plan",
  xfactor_assessment: "XFACTOR assessment",
};

/** Telemetry event name stubs — PostHog wiring deferred */
export const TELEMETRY_EVENTS = {
  LIBRARY_VIEWED: "library_viewed",
  TIP_DETAIL_VIEWED: "tip_detail_viewed",
  FILTER_APPLIED: "filter_applied",
  PAIRED_CARD_CLICKED: "paired_card_clicked",
  MORE_LIKE_THIS_CLICKED: "more_like_this_clicked",
} as const;

export interface SponsorTip {
  id: string;
  category: string;
  tip_type: "best_practice" | "failure_mode";
  related_categories: string[];
  audience: "sponsor" | "account_team" | "program_leader" | "both";
  role_lens?: string | null;
  title: string;
  url_slug: string;
  body: string;
  body_markdown?: string | null;
  email_short: string;
  xfactor_levels: string[];
  seniority: string[];
  behavioral_trigger?: string | null;
  video_candidate?: boolean;
  video_url?: string | null;
  video_thumbnail?: string | null;
  video_duration_seconds?: number | null;
  link?: string | null;
  link_label?: string | null;
  eb_feature_preview?: string | null;
  related_tips: string[];
  paired_pitfall?: string | null;
  active: boolean;
  published_at?: string | null;
  created_at?: string | null;
}

export interface SponsorTipListResponse {
  tips: SponsorTip[];
  total: number;
}

export interface SponsorTipRecommendedResponse extends SponsorTipListResponse {
  sponsor_context?: { xfactorLevels?: string[] };
}

export interface SponsorTipDetailResponse {
  tip: SponsorTip;
  paired_tip: SponsorTip | null;
  more_like_this: SponsorTip[];
}
